import {
  AlertFilled,
  FileTextFilled,
  FireFilled,
  HeartFilled,
  InfoCircleFilled,
  MedicineBoxFilled,
  SmileFilled,
} from '@ant-design/icons';
import CalendarTabs from 'components/cards/CalendarTabs';
import ModuleCard from 'components/cards/ModuleCard';
import { calculateCalendarChartHeight, FertilityDot } from 'components/charts/CalendarChart';
import LinearCalendarChart from 'components/charts/LinearCalendarChart';
import PeriodChart from 'components/charts/PeriodChart';
import SexualActivityChart from 'components/charts/SexualActivityChart';
import SexualActivityChartLegend from 'components/charts/SexualActivityChartLegend';
import SymptomsChart from 'components/charts/SymptomsChart';
import SymptomsChartLegend from 'components/charts/SymptomsChartLegend';
import TemperatureChart from 'components/charts/TemperatureChart';
import {
  VAGINAL_DISCHARGE_COLORS,
  VAGINAL_DISCHARGE_ICON_MAP,
  VAGINAL_DISCHARGE_SHAPE_MAP,
} from 'components/charts/VaginalDischargesColors';
import { Button } from 'components/ui/button';
import { Calendar } from 'components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import dayjs from 'dayjs';
import usePreferences from 'hooks/usePreferences';
import useShouldAskForPeriod from 'hooks/useShouldAskForPeriod';
import useSymptomTrackingData, { getOvulationAndFertilityWindow } from 'hooks/useSymptomTrackingData';
import { cn } from 'lib/utils';
import { locale } from 'locales/i18nManager';
import { CalendarIcon } from 'lucide-react';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

// height reserved, below each chart's own height, for the gap to the next title (24px title height + 32px margin + 4px title-to-chart gap)
const TITLE_ROW_GAP = 24 + 32 + 4;

const DropIcon = ({ className }: { className?: string }) => (
  <svg
    width="1em"
    height="1em"
    viewBox="0 0 14 21"
    fill="currentColor"
    className={cn('inline-block align-[-0.125em]', className)}
  >
    <path d="M14 14C14 17.866 10.866 21 7 21C3.13401 21 0 17.866 0 14C0 10.134 3.13401 0 7 0C10.866 0 14 10.134 14 14Z" />
  </svg>
);

const GRAPH_ICONS: Record<string, React.ReactNode> = {
  generalState: <SmileFilled className="text-chart-severity-very-good" />,
  period: <DropIcon className="text-chart-severity-very-bad" />,
  pain: <AlertFilled className="text-chart-severity-bad" />,
  sexualActivity: <HeartFilled className="text-warning" />,
  bodyTemperature: <FireFilled className="text-destructive-text" />,
  vaginalDischarges: <DropIcon className="text-chart-4" />,
  symptoms: <MedicineBoxFilled className="text-chart-severity-average" />,
  notes: <FileTextFilled className="text-muted-foreground" />,
};

export interface HistoryModuleProps {
  enoughDataForHistory: boolean;
}

const HistoryModule: React.FC<HistoryModuleProps> = ({ enoughDataForHistory }) => {
  const { t } = useTranslation();
  const historyGraphsRef = useRef<HTMLDivElement>(null);
  const today = dayjs();

  // History date management
  const [historyEndDate, setHistoryEndDate] = useState<dayjs.Dayjs>(dayjs());
  const [rangePickerOpen, setRangePickerOpen] = useState(false);
  const historyStartDate = historyEndDate.subtract(3, 'month');

  // Period tracking preferences
  const { isPeriodTrackingEnabled } = useShouldAskForPeriod();

  // Graph visibility preferences
  const { value: visibleGraphsPreference, set: setVisibleGraphsPreference } = usePreferences(
    Keys.TRACKING_HISTORY_VISIBLE_GRAPHS,
  );

  // Default to only period graph if no preference is set
  const defaultVisibleGraphs = isPeriodTrackingEnabled ? ['period'] : ['generalState', 'pain', 'symptoms'];

  // usePreferences only reflects a write after an async Preferences.set() -> get() round-trip, so rapid
  // sequential toggles would otherwise compute their selection from a stale snapshot and silently undo each
  // other. Keep a locally, synchronously updated copy that's the source of truth for the UI once loaded, and
  // only seed it from the persisted preference once (on initial load). The onClick handler uses the functional
  // setState form so it always reads the latest selection rather than a stale closed-over value.
  const [optimisticVisibleGraphs, setOptimisticVisibleGraphs] = useState<string[] | null>(null);
  useEffect(() => {
    if (optimisticVisibleGraphs === null && Array.isArray(visibleGraphsPreference)) {
      setOptimisticVisibleGraphs(visibleGraphsPreference);
    }
  }, [visibleGraphsPreference, optimisticVisibleGraphs]);
  const visibleGraphs = optimisticVisibleGraphs ?? defaultVisibleGraphs;

  // Symptom tracking data
  const { getSpecificDailyStatus, getStatusValueForDisplayedDays, cycleHistory, cycleStats } = useSymptomTrackingData();

  // matches the actual height of each chart, so that titles (which sit in their own, separate column) stay
  // aligned with their corresponding graph - see the titles container comment below
  const standardRowHeight = calculateCalendarChartHeight({ showMonthLabels: true });
  const bodyTemperatureRowHeight = calculateCalendarChartHeight({ showMonthLabels: true, yRangeOverride: 7.5 });
  const sexualActivityRowHeight = calculateCalendarChartHeight({
    hasBottomDots: true,
    showMonthLabels: true,
    yRangeOverride: 2,
  });

  // Fertility window dots, displayed below day numbers on the sexual activity chart
  const fertilityDots = useMemo(() => {
    const dots: FertilityDot[] = [];
    cycleHistory.forEach(cycle => {
      const { ovulationDate, fertileStartDate, fertileEndDate, isFertilityWindowPredictionReliable } =
        getOvulationAndFertilityWindow(cycle, cycleStats);
      if (!isFertilityWindowPredictionReliable) return;
      let day = fertileStartDate;
      while (day.isSameOrBefore(fertileEndDate, 'day')) {
        dots.push({
          date: day,
          type: day.isSame(ovulationDate, 'day') ? 'ovulation' : 'fertile',
          predicted: !cycle.completed,
        });
        day = day.add(1, 'day');
      }
    });
    return dots;
  }, [cycleHistory, cycleStats]);

  // Calculate displayed days
  const displayedDays = useMemo(() => {
    return Array.from({ length: historyEndDate.diff(historyStartDate, 'day') + 1 }, (_, i) =>
      historyStartDate.add(i, 'day').set('hour', 0).set('minute', 0).set('second', 0).set('millisecond', 0),
    );
  }, [historyStartDate, historyEndDate]);

  // Legends (eg. symptoms, vaginal discharges) have content-dependent heights that can't be computed upfront like
  // chart heights can, so we measure them once rendered and use that to keep the graphs column (see graphRows
  // below) aligned with the titles column.
  const [legendHeights, setLegendHeights] = useState<Record<string, number>>({});
  const legendObservers = useRef<Record<string, ResizeObserver>>({});
  const observeLegend = useCallback(
    (key: string) => (el: HTMLDivElement | null) => {
      legendObservers.current[key]?.disconnect();
      delete legendObservers.current[key];
      if (!el) return;
      const observer = new ResizeObserver(entries => {
        const height = entries[0]?.contentRect.height ?? 0;
        setLegendHeights(prev => (prev[key] === height ? prev : { ...prev, [key]: height }));
      });
      observer.observe(el);
      legendObservers.current[key] = observer;
    },
    [],
  );
  useEffect(() => {
    const observers = legendObservers.current;
    return () => Object.values(observers).forEach(observer => observer.disconnect());
  }, []);

  // One entry per visible graph, in display order, holding everything needed to render its title, optional legend
  // and chart in sync across the two overlaid columns described in the titles container comment below.
  const graphRows = useMemo(() => {
    const rows: {
      key: string;
      testId: string;
      title: string;
      icon: React.ReactNode;
      rowHeight: number;
      legend?: React.ReactNode;
      graph: React.ReactNode;
    }[] = [];

    if (visibleGraphs.includes('generalState')) {
      rows.push({
        key: 'generalState',
        testId: 'general-state-graph',
        title: t('symptomTracking.graphs.generalState'),
        icon: GRAPH_ICONS.generalState,
        rowHeight: standardRowHeight,
        graph: (
          <LinearCalendarChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('general_state', displayedDays)}
            range={[0, 4]}
            showMonthLabels
          />
        ),
      });
    }
    if (isPeriodTrackingEnabled && visibleGraphs.includes('period')) {
      rows.push({
        key: 'period',
        testId: 'period-graph',
        title: t('symptomTracking.graphs.menstruation'),
        icon: GRAPH_ICONS.period,
        rowHeight: standardRowHeight,
        graph: (
          <PeriodChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('menstruation_amount', displayedDays)}
            showMonthLabels
          />
        ),
      });
    }
    if (visibleGraphs.includes('pain')) {
      rows.push({
        key: 'pain',
        testId: 'pain-graph',
        title: t('symptomTracking.graphs.pain'),
        icon: GRAPH_ICONS.pain,
        rowHeight: standardRowHeight,
        graph: (
          <LinearCalendarChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('pain_level', displayedDays)}
            range={[0, 4]}
            reverseColors
            showMonthLabels
          />
        ),
      });
    }
    if (visibleGraphs.includes('sexualActivity')) {
      rows.push({
        key: 'sexualActivity',
        testId: 'sexual-activity-graph',
        title: t('symptomTracking.graphs.sexualActivity'),
        icon: GRAPH_ICONS.sexualActivity,
        rowHeight: sexualActivityRowHeight,
        legend: <SexualActivityChartLegend />,
        graph: (
          <SexualActivityChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('sexual_activity', displayedDays, null)}
            fertilityDots={fertilityDots}
            showMonthLabels
          />
        ),
      });
    }
    if (visibleGraphs.includes('bodyTemperature')) {
      rows.push({
        key: 'bodyTemperature',
        testId: 'body-temperature-graph',
        title: t('symptomTracking.graphs.bodyTemperature'),
        icon: GRAPH_ICONS.bodyTemperature,
        rowHeight: bodyTemperatureRowHeight,
        graph: (
          <TemperatureChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('body_temperature', displayedDays)}
            showMonthLabels
          />
        ),
      });
    }
    if (visibleGraphs.includes('vaginalDischarges')) {
      rows.push({
        key: 'vaginalDischarges',
        testId: 'vaginal-discharges-graph',
        title: t('symptomTracking.graphs.vaginalDischarges'),
        icon: GRAPH_ICONS.vaginalDischarges,
        rowHeight: standardRowHeight,
        legend: (
          <SymptomsChartLegend
            dayValues={getStatusValueForDisplayedDays('vaginal_discharges', displayedDays, [])}
            translationKeyPrefix="symptomTracking.survey.vaginalDischarges"
            colorMap={VAGINAL_DISCHARGE_COLORS}
            iconMap={VAGINAL_DISCHARGE_ICON_MAP}
          />
        ),
        graph: (
          <SymptomsChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('vaginal_discharges', displayedDays, [])}
            showMonthLabels
            colorMap={VAGINAL_DISCHARGE_COLORS}
            shapeMap={VAGINAL_DISCHARGE_SHAPE_MAP}
          />
        ),
      });
    }
    if (visibleGraphs.includes('symptoms')) {
      rows.push({
        key: 'symptoms',
        testId: 'symptoms-graph',
        title: t('symptomTracking.graphs.symptoms'),
        icon: GRAPH_ICONS.symptoms,
        rowHeight: standardRowHeight,
        legend: <SymptomsChartLegend dayValues={getStatusValueForDisplayedDays('symptoms', displayedDays, [])} />,
        graph: (
          <SymptomsChart
            dayLabels={displayedDays}
            dayValues={getStatusValueForDisplayedDays('symptoms', displayedDays, [])}
            showMonthLabels
          />
        ),
      });
    }

    return rows;
  }, [
    visibleGraphs,
    isPeriodTrackingEnabled,
    t,
    standardRowHeight,
    bodyTemperatureRowHeight,
    sexualActivityRowHeight,
    displayedDays,
    getStatusValueForDisplayedDays,
    fertilityDots,
  ]);

  // Graph options for the multiselect
  const graphOptions = useMemo(() => {
    const options = [
      { label: t('symptomTracking.graphs.generalState'), value: 'generalState', icon: GRAPH_ICONS.generalState },
      { label: t('symptomTracking.graphs.pain'), value: 'pain', icon: GRAPH_ICONS.pain },
      { label: t('symptomTracking.graphs.sexualActivity'), value: 'sexualActivity', icon: GRAPH_ICONS.sexualActivity },
      {
        label: t('symptomTracking.graphs.bodyTemperature'),
        value: 'bodyTemperature',
        icon: GRAPH_ICONS.bodyTemperature,
      },
      {
        label: t('symptomTracking.graphs.vaginalDischarges'),
        value: 'vaginalDischarges',
        icon: GRAPH_ICONS.vaginalDischarges,
      },
      { label: t('symptomTracking.graphs.symptoms'), value: 'symptoms', icon: GRAPH_ICONS.symptoms },
    ];

    if (isPeriodTrackingEnabled) {
      options.splice(1, 0, {
        label: t('symptomTracking.graphs.menstruation'),
        value: 'period',
        icon: GRAPH_ICONS.period,
      });
    }

    return options;
  }, [t, isPeriodTrackingEnabled]);

  useEffect(() => {
    if (historyGraphsRef.current) {
      historyGraphsRef.current.scrollLeft = historyGraphsRef.current.scrollWidth;
    }
  }, [historyGraphsRef.current, enoughDataForHistory]);

  const ErrorFallback = (props: FallbackProps) => {
    void props.error;
    void props.resetErrorBoundary;
    return (
      <div className="flex h-full items-center justify-center px-8 text-center text-chart-severity-bad">
        <p>{t('symptomTracking.error.title')}</p>
      </div>
    );
  };

  const daysWithNotes = (displayedDays.map(getSpecificDailyStatus).filter((d: any) => d !== undefined) || []).filter(
    status => status.comment || status.extra_symptoms,
  );

  return (
    <ModuleCard
      title={t('symptomTracking.modules.historyTitle')}
      data-testid="history-module"
      topRightContent={
        enoughDataForHistory ? (
          <Popover open={rangePickerOpen} onOpenChange={setRangePickerOpen}>
            <PopoverTrigger asChild>
              {/* The range label is long on a narrow phone: it truncates rather than pushing the title */}
              <Button variant="outline" size="sm" className="max-w-[55vw]" data-testid="history-range-picker">
                <CalendarIcon />
                <span className="truncate">
                  {historyStartDate.toDate().toLocaleDateString(locale, { month: 'short', day: 'numeric' })} -{' '}
                  {historyEndDate.toDate().toLocaleDateString(locale, { month: 'short', day: 'numeric' })}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto p-0">
              <Calendar
                mode="single"
                required
                selected={historyEndDate.toDate()}
                defaultMonth={historyEndDate.toDate()}
                disabled={{ after: today.toDate() }}
                onSelect={value => {
                  setHistoryEndDate(dayjs(value).startOf('day'));
                  setRangePickerOpen(false);
                  posthog.capture('tracking.history_range_changed');
                }}
              />
              <div className="flex justify-center px-3 my-4">
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    setHistoryEndDate(today.startOf('day'));
                    setRangePickerOpen(false);
                    posthog.capture('tracking.history_range_changed');
                  }}
                >
                  {t('common.today')}
                </Button>
              </div>
              <p className="max-w-72 px-3 pb-3 text-sm leading-normal text-muted-foreground [&_svg]:mr-1">
                <InfoCircleFilled /> {t('symptomTracking.history.historyDescription')}
              </p>
            </PopoverContent>
          </Popover>
        ) : null
      }
    >
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(error, errorInfo) => console.error('Error in History', error, errorInfo)}
      >
        <div className="relative mt-3">
          {enoughDataForHistory ? (
            <>
              {/* the `after` pseudo-element fades the right edge of the scrollable selector into the card */}
              <div className="relative mb-1 flex flex-col gap-1 pt-1 after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-10 after:w-14 after:bg-[linear-gradient(to_left,var(--color-card)_0%,transparent_100%)] after:content-['']">
                <div className="flex gap-2 overflow-x-auto overflow-y-visible py-1 pr-14 pl-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {graphOptions.map(option => (
                    <Button
                      key={option.value}
                      variant="secondary"
                      className="shrink-0 whitespace-nowrap data-[selected=false]:text-muted-foreground [&[data-selected=false]_.anticon]:text-muted-foreground [&[data-selected=false]_svg]:text-muted-foreground"
                      selected={visibleGraphs.includes(option.value)}
                      onClick={e => {
                        e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                        setOptimisticVisibleGraphs(prev => {
                          const currentSelection = prev ?? defaultVisibleGraphs;
                          const isCurrentlySelected = currentSelection.includes(option.value);
                          const newSelection = isCurrentlySelected
                            ? currentSelection.filter(graph => graph !== option.value)
                            : [...currentSelection, option.value];
                          if (newSelection.length === 0) return currentSelection;
                          setVisibleGraphsPreference(JSON.stringify(newSelection));
                          posthog.capture('tracking.history_graphs_selection_changed', {
                            selectedGraphs: newSelection,
                          });
                          posthog.capture(
                            isCurrentlySelected ? 'tracking.history_graph_hidden' : 'tracking.history_graph_shown',
                            { selectedGraph: option.value },
                          );
                          return newSelection;
                        });
                      }}
                    >
                      {option.icon} {option.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="relative mt-3">
                {/*
                  This titles column is overlaid with the graphs container below. Titles don't share a single
                  fixed gap since not all graphs have the same height (eg. the sexual activity graph is more
                  compact) - each title gets an explicit marginBottom matching its own graph's height instead.
                  Each title's legend (if any) is rendered right after it, so it lands directly below its own
                  graph rather than at the bottom of the column - the matching graph row gets its own height
                  bumped by the legend's measured height to stay aligned, see legendHeights/graphRows above.
                */}
                <div className="flex flex-col">
                  {graphRows.map(row => (
                    <Fragment key={row.key}>
                      <h3
                        className="mt-8 h-6 text-muted-foreground text-lg"
                        data-testid={row.testId}
                        style={{ marginBottom: row.rowHeight + 4 }}
                      >
                        {row.icon} {row.title}
                      </h3>
                      {row.legend && (
                        <div ref={observeLegend(row.key)} data-testid={`${row.testId}-legend`}>
                          {row.legend}
                        </div>
                      )}
                    </Fragment>
                  ))}
                </div>
                {/*
                  This container is overlaid on top of the titles column: it allows smooth scrolling of all
                  graphs together. Its top padding is the first title's height + gap.
                */}
                <div
                  className="absolute inset-x-0 top-0 flex flex-col overflow-x-auto overflow-y-hidden [scrollbar-width:none]"
                  style={{ paddingTop: TITLE_ROW_GAP }}
                  ref={historyGraphsRef}
                >
                  {graphRows.map((row, index) => (
                    <div
                      key={row.key}
                      style={
                        index < graphRows.length - 1
                          ? { marginBottom: TITLE_ROW_GAP + (legendHeights[row.key] ?? 0) }
                          : undefined
                      }
                    >
                      {row.graph}
                    </div>
                  ))}
                </div>
                {daysWithNotes.length > 0 && (
                  <>
                    <h3 className="mt-6 mb-2 text-muted-foreground text-lg">
                      {GRAPH_ICONS.notes} {t('symptomTracking.graphs.notes')}
                    </h3>
                    <CalendarTabs daysWithNotes={daysWithNotes} />
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="overflow-hidden">
              <div className="relative">
                <LinearCalendarChart
                  dayLabels={Array.from({ length: 40 }, (_, i) => today.subtract(i, 'days')).reverse()}
                  dayValues={Array(2).fill([0, 0, 2, 0, 0, 0, 0, 0, 0, 0, 1, 2, 3, 1, 0, 0, 1, 0, 0, 0]).flat()}
                  range={[0, 4]}
                  reverseColors
                  showMonthLabels
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-card/75 p-4 text-center text-sm text-subtle-foreground backdrop-blur-xs [&_p]:m-0">
                  <div className="rounded-md bg-[radial-gradient(closest-side,var(--color-card),transparent)] px-9 py-12 text-center text-foreground">
                    <p className="text-center text-muted-foreground">Pas de données</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ErrorBoundary>
    </ModuleCard>
  );
};

export default HistoryModule;
